// Equation — BlockNote 커스텀 블록. KaTeX 수학 수식(display mode).
//
// - props.latex: LaTeX 소스 문자열. 기본 ''
// - content: 'none' — 블록 내부 inline content 없음(y-prosemirror 추적 최소화, LinkedDatabaseView 패턴).
// - 편집: 클릭 → textarea + 실시간 미리보기. Enter/blur 확정, Shift+Enter 줄바꿈, Esc 취소.
// - 읽기 전용(공개 페이지·잠금): editor.isEditable === false 면 렌더만 하고 클릭 편집 비활성.
//
// ⚠️ BlockNote 0.51.4: createReactBlockSpec(config, impl) 는 "생성 함수" 를 반환한다.
//    반드시 끝에 () 로 한 번 더 호출해야 실제 BlockSpec 객체가 된다(LinkedDatabaseView.jsx 참조).
//    누락 시 에디터 초기화 크래시 → 전 페이지 백지. (2026-06-18 사고)

import { useEffect, useRef, useState } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './custom-blocks.css';

// LaTeX → HTML. throwOnError:false 로 대부분의 ParseError 는 빨간 텍스트로 렌더되지만,
// 그 외 예외도 있어 try/catch 로 이중 방어(오류 시 message 를 빨간 안내로 표시).
function renderKatex(latex) {
  try {
    return {
      html: katex.renderToString(latex, {
        displayMode: true,
        throwOnError: false,
        errorColor: '#d33',
      }),
    };
  } catch (e) {
    return { error: (e && e.message) || '수식을 렌더링할 수 없습니다' };
  }
}

function EquationView({ block, editor }) {
  const editable = editor.isEditable;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.props.latex || '');
  const taRef = useRef(null);

  // 편집 시작 시 textarea 포커스 + 커서 끝으로.
  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      const len = taRef.current.value.length;
      taRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(block.props.latex || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if ((block.props.latex || '') !== draft) {
      editor.updateBlock(block, { props: { latex: draft } });
    }
  };

  const cancel = () => {
    setDraft(block.props.latex || '');
    setEditing(false);
  };

  // 편집 중엔 draft 를 실시간 미리보기, 아니면 확정된 props.latex 를 렌더.
  const latex = editing ? draft : (block.props.latex || '');
  const rendered = latex.trim() ? renderKatex(latex) : null;

  return (
    <div className="bn-equation" contentEditable={false}>
      {editing ? (
        <div className="bn-equation-edit">
          <textarea
            ref={taRef}
            className="bn-equation-input"
            value={draft}
            rows={3}
            placeholder="수식을 입력하세요 (LaTeX)"
            spellCheck={false}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
          />
          <div className="bn-equation-hint">Enter 확정 · Shift+Enter 줄바꿈 · Esc 취소</div>
        </div>
      ) : null}
      <div
        className={`bn-equation-view${!latex.trim() ? ' is-empty' : ''}${editable && !editing ? ' is-clickable' : ''}`}
        onClick={editable && !editing ? startEdit : undefined}
        role={editable && !editing ? 'button' : undefined}
        title={editable && !editing ? '클릭해서 수식 편집' : undefined}
      >
        {!latex.trim() ? (
          <span className="bn-equation-placeholder">수식을 입력하세요 (LaTeX)</span>
        ) : rendered.error ? (
          <span className="bn-equation-error">수식 오류: {rendered.error}</span>
        ) : (
          // KaTeX 출력물은 로컬 라이브러리 생성 HTML(사용자 raw HTML 아님) — XSS 안전.
          <span dangerouslySetInnerHTML={{ __html: rendered.html }} />
        )}
      </div>
    </div>
  );
}

export const equationSpec = createReactBlockSpec(
  {
    type: 'equation',
    propSchema: {
      latex: { default: '' },
    },
    content: 'none',
  },
  {
    render: ({ block, editor }) => <EquationView block={block} editor={editor} />,
  },
)();

export default equationSpec;
